// SpaceContentUploadPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react"; // ← 로딩 아이콘

export function SpaceContentUploadPage() {
    const [eventName, setEventName] = useState("");
    const [location, setLocation] = useState("");
    const [locationType, setLocationType] = useState("");
    const [files, setFiles] = useState([]);

    const [keywords, setKeywords] = useState([]);
    const [themes, setThemes] = useState([]);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [selectedThemes, setSelectedThemes] = useState([]);

    const [manualKeyword, setManualKeyword] = useState("");
    const [manualTheme, setManualTheme] = useState("");

    // 업로드 로딩 상태 추가
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/space-content/keywords`)
            .then((res) => {
                // 쉼표로 구분된 문자열 배열인지 확인 후 분리
                const raw = res.data; // 예: ["키워드1,키워드2"]
                const parsed = raw
                    .flatMap((k) => k.split(",").map((s) => s.trim()))
                    .filter(Boolean);
                setKeywords(Array.from(new Set(parsed)));
            });

        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/space-content/themes`)
            .then((res) => {
                const raw = res.data; // 예: ["테마1,테마2"]
                const parsed = raw
                    .flatMap((t) => t.split(",").map((s) => s.trim()))
                    .filter(Boolean);
                setThemes(Array.from(new Set(parsed)));
            });
    }, []);

    const toggle = (list, setList, value) => {
        setList((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const handleSubmit = async () => {
        // 필수 항목 확인
        if (!eventName.trim() || !location.trim() || !locationType.trim()) {
            alert("행사명, 장소명, 장소유형을 모두 입력해주세요.");
            return;
        }
        if (selectedKeywords.length === 0) {
            alert("키워드를 하나 이상 선택해주세요.");
            return;
        }
        if (selectedThemes.length === 0) {
            alert("행사 성격을 하나 이상 선택해주세요.");
            return;
        }
        if (files.length === 0) {
            alert("파일을 하나 이상 업로드해주세요.");
            return;
        }

        const formData = new FormData();
        formData.append("eventName", eventName);
        formData.append("location", location);
        formData.append("locationType", locationType);
        selectedKeywords.forEach((k) => formData.append("keywords", k));
        selectedThemes.forEach((t) => formData.append("themes", t));
        files.forEach((file) => formData.append("files", file));

        // 업로드 시작 시 로딩 상태 true
        setLoading(true);

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/space-content/upload`,
                formData
            );
            alert("업로드 완료");
            // 폼 초기화
            setEventName("");
            setLocation("");
            setLocationType("");
            setFiles([]);
            setSelectedKeywords([]);
            setSelectedThemes([]);
        } catch (e) {
            alert("업로드 실패(중복된 행사명이 있는지 확인해주세요)");
        } finally {
            // 업로드 끝나면 로딩 해제
            setLoading(false);
        }
    };

    const handleManualKeywordKeyDown = (e) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === "Enter" && manualKeyword.trim()) {

            if ( manualKeyword.length <= 1 ) {
                return
            }

            e.preventDefault();
            const keyword = manualKeyword.trim();
            if (keyword.includes(",")) {
                alert("쉼표(,)는 입력할 수 없습니다.");
                return;
            }
            if (!keywords.includes(keyword)) {
                setKeywords((prev) => [...prev, keyword]);
            }
            if (!selectedKeywords.includes(keyword)) {
                setSelectedKeywords((prev) => [...prev, keyword]);
            }
            setManualKeyword("");
        }
    };

    const handleManualThemeKeyDown = (e) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === "Enter" && manualTheme.trim()) {

            if ( manualTheme.length <= 1 ) {
                setManualTheme("")
                return
            }

            e.preventDefault();
            const theme = manualTheme.trim();
            if (theme.includes(",")) {
                alert("쉼표(,)는 입력할 수 없습니다.");
                return;
            }
            if (!themes.includes(theme)) {
                setThemes((prev) => [...prev, theme]);
            }
            if (!selectedThemes.includes(theme)) {
                setSelectedThemes((prev) => [...prev, theme]);
            }
            setManualTheme("");
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const existingFileNames = new Set(files.map((f) => f.name));
        const uniqueFiles = selectedFiles.filter((f) => !existingFileNames.has(f.name));
        setFiles((prev) => [...prev, ...uniqueFiles]);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="slow-animation p-4 md:p-6 lg:p-8">
            <div className="max-w-2xl mx-auto bg-white shadow-md rounded-lg p-6 space-y-6">
                <h2 className="text-2xl font-bold">공간 콘텐츠 업로드</h2>

                {/* 행사명 */}
                <div>
                    <label
                        htmlFor="eventName"
                        className="block text-sm font-semibold mb-1"
                    >
                        행사명
                    </label>
                    <input
                        id="eventName"
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                        placeholder="행사명을 입력하세요"
                        className="border border-gray-300 w-full rounded px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* 장소명 */}
                <div>
                    <label
                        htmlFor="location"
                        className="block text-sm font-semibold mb-1"
                    >
                        장소명
                    </label>
                    <input
                        id="location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="장소명을 입력하세요"
                        className="border border-gray-300 w-full rounded px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* 장소 유형 (실내/실외) */}
                <div>
                    <label className="block text-sm font-semibold mb-2">
                        장소유형
                    </label>
                    <div className="flex gap-4">
                        {["실내", "실외"].map((v) => (
                            <button
                                key={v}
                                onClick={() => setLocationType(v)}
                                className={`border px-3 py-1 rounded-full text-sm transition-colors
                                    ${
                                    locationType === v
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {v}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 키워드 */}
                <div>
                    <label className="block text-sm font-semibold mb-2">키워드</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {keywords.map((k) => (
                            <button
                                key={k}
                                onClick={() => toggle(selectedKeywords, setSelectedKeywords, k)}
                                className={`border px-2 py-1 rounded-full text-sm transition
                                    ${
                                    selectedKeywords.includes(k)
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200"
                                }`}
                            >
                                {k}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        value={manualKeyword}
                        onChange={(e) => setManualKeyword(e.target.value)}
                        onKeyDown={handleManualKeywordKeyDown}
                        placeholder="추가 키워드 입력 후 Enter"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* 행사 성격 (themes) */}
                <div>
                    <label className="block text-sm font-semibold mb-2">행사 성격</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {themes.map((t) => (
                            <button
                                key={t}
                                onClick={() => toggle(selectedThemes, setSelectedThemes, t)}
                                className={`border px-2 py-1 rounded-full text-sm transition
                                    ${
                                    selectedThemes.includes(t)
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        value={manualTheme}
                        onChange={(e) => setManualTheme(e.target.value)}
                        onKeyDown={handleManualThemeKeyDown}
                        placeholder="추가 행사 성격 입력 후 Enter"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* 파일 업로드 */}
                <div>
                    <label className="block text-sm font-semibold mb-2">파일 업로드(여러 파일을 업로드 가능합니다)</label>
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        accept=".png,.jpg,.jpeg"
                        className="block w-full text-sm text-gray-500
                                   file:mr-4 file:py-2 file:px-4
                                   file:rounded-full file:border-0
                                   file:text-sm file:font-semibold
                                   file:bg-blue-50 file:text-blue-700
                                   hover:file:bg-blue-100
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    {files.length > 0 && (
                        <div className="space-y-2 mt-3">
                            {files.map((file, idx) => (
                                <div
                                    key={idx}
                                    className="flex justify-between items-center text-sm
                                               border px-3 py-1 rounded bg-gray-50"
                                >
                                    <div className="truncate max-w-xs">{file.name}</div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-gray-600">
                                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                                        </span>
                                        <button
                                            className="text-red-500 hover:text-red-700 transition"
                                            onClick={() => removeFile(idx)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end">
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    >
                        저장
                    </button>
                </div>
            </div>

            {/* 로딩 오버레이 (loading이 true일 때만) */}
            {loading && (
                <div className="fixed inset-0 bg-gray-100 bg-opacity-40 flex items-start justify-center pt-[30vh] z-50">
                    <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                        <Loader2 className="animate-spin w-5 h-5 text-blue-600" />
                        업로드 중입니다...
                    </div>
                </div>
            )}
        </div>
    );
}